import { ApolloClient } from '@apollo/client';
import type { ClientsideConfiguration } from 'Global';
import { History } from 'history';
import { action, computed, observable } from 'mobx';
import { createContext } from 'react';
import { IGlobalModal } from 'Views/Components/Modal/GlobalModal';
import type { IntercomConfiguration, StripeConfiguration } from 'Global';
import { OrganisationEntity, UserEntity } from 'Models/Entities';
import AppSettings from './AppSettings';
import { UserTypeOrder, type userType } from './Enums';
import { calculateUserPermissions, UserPermissions } from './UserPermissions';

export interface IGroupResult {
	name: string;
	hasBackendAccess: boolean;
}

export interface IUserResult {
	type: 'user-data';
	id: string;
	userName: string;
	userDisplayName: string;
	email: string;
	groups: IGroupResult[];
	userType: userType;
	organisation?: OrganisationEntity;
	canAccessIntel?: boolean;
	canAccessPPSR?: boolean;
	canAccessApprove?: boolean;
	userPermissions?: UserPermissions;
	termsUpdated?: Date;
	// Business user is linked to
	businessEntityIds?: string[];
	creditApprovalLimit?: number;
}

export interface IStore {
	/**
	 * The current location in the application
	 */
	appLocation: 'frontend' | 'admin';

	/**
	 * The router history object for React Router
	 */
	routerHistory: History;

	/**
	 * The client for Apollo
	 */
	apolloClient: ApolloClient<Record<string, unknown>>;

	/**
	 * The global modal that is stored in the app and can be called imperatively
	 */
	modal: IGlobalModal;

	/**
	 * This signifies weather we are logged in or not
	 * Only ever set this value to true if there is a value set in this.token
	 */
	readonly loggedIn: boolean;

	/**
	 * The user Id of the logged-in user
	 */
	readonly userId: string | undefined;

	/**
	 * The user name of the logged in user
	 */
	readonly userName: string | undefined;

	/**
	 * The email of the current logged in user
	 */
	readonly email: string | undefined;

	/**
	 * The groups that the logged in user are a part of
	 */
	readonly userGroups: IGroupResult[];

	/**
	 * Does this user have access to the backend admin views
	 */
	readonly hasBackendAccess: boolean;

	/**
	* The configuration sent from the serverside
	*/
	readonly configuration: ClientsideConfiguration;

	/**
	 * Is the frontend in edit mode
	 */
	frontendEditMode: boolean;

	/**
	 * Sets the current logged in user in the store
	 * @param userResult
	 */
	setLoggedInUser(userResult: IUserResult): void;

	/**
	 * Clears the logged in user data from the store
	 */
	clearLoggedInUser(): void;

	readonly creditApprovalLimit?: number;
	getUser?: IUserResult;
	readonly userDisplayName: string;
	canAccessIntel: boolean;
	canAccessApprove: boolean;
	canAccessPPSR: boolean;
	userType: userType;
	userPermissions: UserPermissions;
	/**
	 * This is used for the super user to impersonate another organisation. 
	 * This gets cleared when switching to the hub from the monitor platform. 
	 * See Monitor Link onClick in ProductSwitcher
	 */
	impersonatingOrganisationId: string;
	/**
	 * The organisation id to act on behalf of: the impersonated organisation if set,
	 * otherwise the logged in user's own organisation.
	 */
	readonly effectiveOrganisationId: string;
	appSettings: AppSettings;
	defaultUserPermissions: {[key in userType]: UserPermissions};
	intercomOptions: IntercomConfiguration;

	/**
	 * Whether the logged in user can edit this user.
	 */
	canEditThisUser: (user: UserEntity) => boolean;
}

/**
 * A global singleton store that contains a global state of data
 */
export class Store implements IStore {
	@observable
		user?: IUserResult;

	@observable
		appLocation: 'frontend' | 'admin' = 'frontend';

	@observable
		clientsideConfiguration: ClientsideConfiguration;

	routerHistory: History;

	apolloClient: ApolloClient<Record<string, unknown>>;

	modal: IGlobalModal;

	@computed
	public get loggedIn() {
		
		return this.user !== undefined;
		
	}

	@computed
	public get userId(): string | undefined {
		
		return this.user ? this.user.id : undefined;
		
	}

	@computed
	public get userName(): string | undefined {
		
		return this.user?.userName;
		
	}

	@computed
	public get email(): string | undefined {
		
		return this.user ? this.user.email : undefined;
		
	}

	@computed
	public get userGroups(): IGroupResult[] {
		
		if (this.user) {
			return [...this.user.groups];
		}
		return [];
		
	}

	@computed
	public get hasBackendAccess() {
		if (this.user) {
			return this.user.groups.some(ug => ug.hasBackendAccess) || this.user.userType === 'SUPER_USER';
		}
		return false;
	}

	@computed
	public get configuration(): ClientsideConfiguration {
		return this.clientsideConfiguration;
	}

	@observable
	public frontendEditMode = false;

	@action
	public setLoggedInUser(userResult: IUserResult) {
		this.user = userResult;
		// Set impersonatingOrganisationId to the id of the organisation we received.
		this.impersonatingOrganisationId = this.user.organisation?.id ?? '';

		// If user is a super, we give them backend access
		// We would just fix hasBackendAccess(), but Admin.tsx manually checks the groups
		if (this.user.userType === 'SUPER_USER') {
			this.user.groups.push({
				name: 'SUPER_USER', // Not a real group, but the name doesn't matter
				hasBackendAccess: true,
			});
		}
	}

	@action
	public clearLoggedInUser() {
		this.user = undefined;
	}

	@action
	public setClientsideDataConfiguration(config: ClientsideConfiguration) {
		this.clientsideConfiguration = config;
	}

	constructor() {

		this.configureUser();
		this.configureClientsideData();

		if (window?.static?.appSettings) {
			this.appSettings = window.static.appSettings;
		}
		if (window?.static?.defaultUserPermissions) {
			this.defaultUserPermissions = window.static.defaultUserPermissions;
		}
		if (window?.data?.userResult && !this.user) {
			this.setLoggedInUser(window.data.userResult);
		}
		this.configureIntercomOptions();
		
	}
	
	canEditThisUser(user: UserEntity) {
		const isAboveLoggedInUser = UserTypeOrder[user.userType] > UserTypeOrder[store.userType];

		// 1. You can manage yourself
		if (user.id === store.userId) return true;

		// 2. You can manage yourself, but this user is not the logged in user due to guard 1.
		if (store.userPermissions.commonManageUsers === 'SELF') return false;

		// 3. You can manage users you can see but they cant be higher than you
		return isAboveLoggedInUser ? false : true;
	}

	configureUser() {
		if (window.loginData && !this.user) {
			this.setLoggedInUser(window.loginData);
		}
	}

	configureClientsideData() {
		const parsedData: ClientsideConfiguration = {
			...window.clientsideDataConfiguration,
		};
		this.setClientsideDataConfiguration(parsedData);
		
	}

	public appSettings: AppSettings;
	public defaultUserPermissions: {[key in userType]: UserPermissions};

	// Set impersonatingOrganisationId to the id of the organisation we received.
	@observable
	public impersonatingOrganisationId: string = this.user?.organisation?.id ?? '';

	@observable
		intercomOptions: IntercomConfiguration;

	@action
	public setIntercomOptions(intercomOptions: IntercomConfiguration) {
		this.intercomOptions = intercomOptions;
	}

	configureIntercomOptions() {
		this.setIntercomOptions(window.intercomData);
	}

	@observable stripeOptions: StripeConfiguration;
	@action
	public setStripeOptions(stripeOptions: StripeConfiguration) {
		this.stripeOptions = stripeOptions;
	}

	configureStripeOptions() {
		this.setStripeOptions(window.stripeData);
	}

	@computed
	public get getUser(): IUserResult|undefined {
		return this.user;
	}

	/**
	 * The organisation id to act on behalf of: the impersonated organisation if set,
	 * otherwise the logged in user's own organisation.
	 */
	@computed
	public get effectiveOrganisationId(): string {
		return this.impersonatingOrganisationId ?? this.user?.organisation?.id ?? '';
	}

	@computed
	public get userDisplayName(): string {
		return this.user?.userDisplayName ?? this.user?.email ?? '';
	}

	@computed
	public get canAccessIntel(): boolean {
		return this.user?.canAccessIntel === true;
	}

	@computed
	public get canAccessApprove(): boolean {
		return this.user?.canAccessApprove === true;
	}

	@computed
	public get canAccessPPSR(): boolean {
		return this.user?.canAccessPPSR === true;
	}

	@computed
	public get userType(): userType {
		return this.user?.userType || 'USER';
	}

	@computed
	public get userPermissions(): UserPermissions {
		return this.user?.userPermissions || calculateUserPermissions(this.userType);
	}

	@computed
	public get creditApprovalLimit(): number | undefined {
		return this.user?.creditApprovalLimit;
	}
	
}

export const store: IStore = new Store();
export const StoreContext = createContext<IStore>(store);
